import { useCallback, useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'

import { SalonPageShell } from '@/components/layout/SalonPageShell'
import { Button } from '@/components/ui/button'
import { fetchCashBook, fetchChartOfAccounts, fetchDayBook, formatLedgerMoney } from '@/lib/api/accounts'
import { apiErrorMessage } from '@/lib/apiErrorMessage'

const TABS = [
  { id: 'coa', label: 'Chart of accounts' },
  { id: 'cash', label: 'Cash book' },
  { id: 'day', label: 'Day book' },
]

export function AccountsDeskPage() {
  const [tab, setTab] = useState('coa')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [accounts, setAccounts] = useState([])
  const [cashBook, setCashBook] = useState({ entries: [] })
  const [dayBook, setDayBook] = useState({ entries: [] })

  const refresh = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [coa, cash, day] = await Promise.all([
        fetchChartOfAccounts(),
        fetchCashBook().catch(() => ({ entries: [] })),
        fetchDayBook().catch(() => ({ entries: [] })),
      ])
      setAccounts(coa ?? [])
      setCashBook(cash ?? { entries: [] })
      setDayBook(day ?? { entries: [] })
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to load accounts.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const cashEntries = cashBook.entries ?? cashBook.rows ?? cashBook.lines ?? []
  const dayEntries = dayBook.entries ?? dayBook.rows ?? dayBook.lines ?? []

  return (
    <SalonPageShell
      title="Accounts"
      description="Chart of accounts, cash book, and day book — read-focused desk view."
    >
      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.map((item) => (
          <Button
            key={item.id}
            type="button"
            size="sm"
            variant={tab === item.id ? 'default' : 'outline'}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </Button>
        ))}
      </div>

      {error ? (
        <div className="mb-4 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </p>
      ) : null}

      {!loading && tab === 'coa' ? (
        <div className="overflow-x-auto rounded-2xl border border-border/70 bg-card">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-border/60 bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Type</th>
              </tr>
            </thead>
            <tbody>
              {accounts.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-4 text-muted-foreground">
                    No accounts found.
                  </td>
                </tr>
              ) : (
                accounts.map((account) => (
                  <tr key={account.id} className="border-b border-border/40">
                    <td className="px-4 py-2 font-medium">{account.code}</td>
                    <td className="px-4 py-2">{account.name}</td>
                    <td className="px-4 py-2 capitalize text-muted-foreground">
                      {account.account_type ?? account.type ?? '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      {!loading && tab === 'cash' ? (
        <LedgerTable entries={cashEntries} empty="No cash book entries." />
      ) : null}

      {!loading && tab === 'day' ? (
        <LedgerTable entries={dayEntries} empty="No day book entries." />
      ) : null}
    </SalonPageShell>
  )
}

/**
 * @param {{ entries: object[], empty: string }} props
 */
function LedgerTable({ entries, empty }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-border/70 bg-card">
      <table className="min-w-full text-left text-sm">
        <thead className="border-b border-border/60 bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-4 py-3">Date</th>
            <th className="px-4 py-3">Narration</th>
            <th className="px-4 py-3">Debit</th>
            <th className="px-4 py-3">Credit</th>
          </tr>
        </thead>
        <tbody>
          {entries.length === 0 ? (
            <tr>
              <td colSpan={4} className="px-4 py-4 text-muted-foreground">
                {empty}
              </td>
            </tr>
          ) : (
            entries.slice(0, 100).map((row, index) => (
              <tr key={row.id ?? index} className="border-b border-border/40">
                <td className="px-4 py-2 whitespace-nowrap">
                  {row.date ?? row.voucher_date ?? row.entry_date ?? '—'}
                </td>
                <td className="px-4 py-2">{row.narration ?? row.description ?? row.account_name ?? '—'}</td>
                <td className="px-4 py-2">{formatLedgerMoney(row.debit)}</td>
                <td className="px-4 py-2">{formatLedgerMoney(row.credit)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}
